import React from 'react'
import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { FREQUENCY } from '@classy/campaign-page-blocks'
import paypalApi from 'redux/services/paypal'
import {
  setPaymentMethod,
  setPaymentType,
} from 'modules/order/state/payment-details/payment-details-slice'
import { PaymentMethod } from 'models/ChargeIntent'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import { getClassySessionId } from 'modules/utils/getClassySessionId'
import useFinalizeSingleStepOrder from '../../../../order/finalize-order/use-finalize-single-step-order'
import Bugsnag from '@bugsnag/js'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'
import {
  validateForms,
  saveFormsData,
} from '../../steps/single/utils/single-step-form-orchestrator'

import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'
import { getCookieValue } from 'modules/utils/getCookieValue'

interface PaypalButtonWrapperProps {
  fee: number
  fundingSource: 'paypal' | 'venmo'
}

// Custom component to wrap the PayPalButtons and handle currency changes
const PaypalButtonWrapper = ({ fundingSource }: PaypalButtonWrapperProps) => {
  const order = useOrder()
  const config = useConfig()
  const appDispatch = useDispatch<TypedDispatch<RootState>>()
  const status = useStatus()

  /**
   * Determine the correct amount to send to PayPal based on DCF state.
   *
   * If DCF is enabled and checked by the donor, always use the total amount with fees.
   * This ensures that PayPal shows the correct amount that the donor has agreed to pay.
   *
   * If dcfNudge.enabled = true, donor has already seen a dcf checkbox in the flow
   * and dcf.value.totalAmount will show the amount with or without DCF based on their choice.
   *
   * If dcfNudge.enabled = false but DCF is still enabled (e.g., in single-step checkout),
   * the donor can still see and interact with the DCF checkbox. In this case, we should
   * respect their choice and use totalAmount if they've checked DCF.
   *
   * Only use the base amount if DCF is not enabled or not checked by the donor.
   */
  const amount =
    config.dcfNudge.enabled || (config.dcf.isEnabled && order.dcf.value.isChecked)
      ? `${order.dcf.value.totalAmount}`
      : `${order.amount.value}`

  const campaignId = config.campaignId
  const currency = order.currency.value
  const frequency = order.frequency.value

  const isRecurring = frequency !== FREQUENCY.ONE_TIME

  const paypalOptions = usePaypalOptions(fundingSource)

  const setPaypalOrderId = useSetPaypalOrderId(fundingSource)

  const handlePayPalError = React.useCallback(
    (error: Record<string, unknown>) => {
      console.error('PayPal error:', error)

      const err =
        error instanceof Error
          ? error
          : new Error((error?.message as string) || 'Unknown PayPal error')

      Bugsnag.notify(err, (event) => {
        event.context = 'PayPal Button Error'

        event.addMetadata('paypal', {
          fundingSource,
          amount,
          currency,
          errorType: err.name,
          errorMessage: err.message,
        })
      })

      status.set(StatusType.FAILED, err.message)
    },
    [fundingSource, amount, currency, status],
  )

  if (paypalOptions.options.enableFunding != fundingSource) {
    // This forces a full re-render if the user toggles between paypal & venmo on the
    // payment selection screen. (The forceReRender param below doesn't seem to work for that)
    return <div></div>
  }

  if (paypalOptions.isLoading)
    return <Placeloader style={{ width: '100%', height: 54, borderRadius: 8 }} />

  return (
    <div style={{ zIndex: 1, height: 54, borderRadius: '12px', overflow: 'hidden' }}>
      <PayPalButtons
        data-id="payment-method-button-PayPal"
        disabled={false}
        onError={handlePayPalError}
        fundingSource={fundingSource}
        forceReRender={[amount, currency, frequency]}
        style={{ layout: 'vertical', shape: 'rect', height: 54 }}
        /**
         * For onetime order
         */
        createOrder={
          !isRecurring
            ? async () => {
                // Validate all forms and save data before creating PayPal order
                if (!config.isEmbedded) {
                  const validationResult = await validateForms()

                  if (!validationResult.success) {
                    throw new Error(validationResult.error || 'Please fill in all required fields')
                  }

                  saveFormsData(validationResult.results || [], order)
                }

                const body = {
                  campaignId,
                  amount,
                  currency,
                  paymentMethod:
                    fundingSource == 'venmo' ? PaymentMethod.Venmo : PaymentMethod.PayPal_Commerce,
                }

                const paypalOrder = await appDispatch(
                  paypalApi.endpoints.createOrder.initiate(body),
                ).unwrap()

                return paypalOrder.orderId
              }
            : undefined
        }
        /**
         * For recurring order
         */
        createBillingAgreement={
          isRecurring
            ? async () => {
                // Validate all forms and save data before creating PayPal billing agreement
                if (!config.isEmbedded) {
                  const validationResult = await validateForms()

                  if (!validationResult.success) {
                    throw new Error(validationResult.error || 'Please fill in all required fields')
                  }

                  saveFormsData(validationResult.results || [], order)
                }

                const response = await appDispatch(
                  paypalApi.endpoints.getPaypalBillingAgreement.initiate({
                    campaignId,
                    currency,
                  }),
                ).unwrap()

                return response.billingAgreementToken
              }
            : undefined
        }
        onApprove={async (data) => {
          const orderId = isRecurring ? data.billingToken : data.orderID

          if (!orderId) return

          setPaypalOrderId(orderId)

          if (typeof window !== 'undefined' && window.newrelic) {
            window.newrelic.addPageAction('payment_method_selected', {
              pgdid: getCookieValue('pgdid'),
              'classy-session-id': getClassySessionId(),
              paymentMethod: PaymentMethod.PayPal_Commerce,
            })
          }
        }}
      />
    </div>
  )
}

export default PaypalButtonWrapper

const useSetPaypalOrderId = (fundingSource: PaypalButtonWrapperProps['fundingSource']) => {
  const order = useOrder()
  const dispatch = useDispatch<TypedDispatch<RootState>>()

  const trackEvent = useTrackUIElementEvent()
  const finalizeSingleStepOrder = useFinalizeSingleStepOrder()

  const isRecurring = order.frequency.value !== FREQUENCY.ONE_TIME

  const [orderId, setOrderId] = React.useState('')

  const { data: oneTimeOrder } = paypalApi.useGetOrderQuery(
    { orderId },
    { skip: !orderId || isRecurring },
  )

  const { data: recurringOrder } = paypalApi.useGetBillingAgreementTokenDetailsQuery(
    { billingAgreementTokenId: orderId },
    { skip: !orderId || !isRecurring },
  )

  const paypalOrder = isRecurring ? recurringOrder : oneTimeOrder

  React.useEffect(() => {
    if (!paypalOrder) return

    order.contact.setValue({
      ...order.contact.value,
      email: paypalOrder.email,
      firstName: paypalOrder.first_name,
      lastName: paypalOrder.last_name,
    })

    order.address.setValue({
      city: paypalOrder.city,
      country: paypalOrder.country_code,
      line1: paypalOrder.address_line_1,
      zipCode: paypalOrder.postal_code,
      state: paypalOrder.state,
    })

    const isVenmo = fundingSource == 'venmo'
    dispatch(setPaymentMethod(isRecurring ? { agreementId: orderId } : { orderId }))

    dispatch(setPaymentType(isVenmo ? PaymentMethod.Venmo : PaymentMethod.PayPal_Commerce))

    trackEvent('Donation Payment Details Submitted', mParticleSdk.EventType.Other)

    finalizeSingleStepOrder()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalOrder])

  return setOrderId
}

const usePaypalOptions = (fundingSource: PaypalButtonWrapperProps['fundingSource']) => {
  const order = useOrder()

  const [{ options, isPending, isResolved }, paypalDispatch] = usePayPalScriptReducer()

  React.useEffect(() => {
    if (options.enableFunding === fundingSource && (isPending || isResolved)) return

    paypalDispatch({
      type: 'resetOptions',
      value: { ...options, enableFunding: fundingSource, currency: order.currency.value },
    })
  }, [options, isPending, isResolved, fundingSource, order.currency.value, paypalDispatch])

  return { options, isLoading: isPending }
}
